import {
  ComponentProps, ComponentType, PropsWithChildren,
  useMemo,
} from 'react';
import {
  Collapsible,
  ErrorTypes,
  Errors,
  flattenErrorTypeArray,
  PaddedCollapsible,
  useCreateAndAddRefs,
  WarningInfoDanger,
  WarningInfo,
  InfoList,
  FlexColumn,
  groupErrorsByNonProperty,
  groupErrorsByProperty,
  TooltipInfoIcon,
  Tooltip,
  Flex,
  ThemeType,
  LockIcon,
} from '@equifinance/shared';
import styled, { FlattenInterpolation, ThemeProps } from 'styled-components';
import { SectionTitleIcon } from '../Icons/SectionTitleIcon';
import { usePageConfigurationContext } from '../../../contexts/pageConfiguration';

export type SectionProps = ComponentProps<typeof Collapsible> & PropsWithChildren<{
  refName?: string,
  propertyName?: string,
  errors?: Errors,
  warnings?: Errors,
  isLocked?: boolean,
  CollapsibleComponent?: ComponentType<ComponentProps<typeof Collapsible>>,
  errorContainerCss?: FlattenInterpolation<ThemeProps<ThemeType>> | string
}>;

const ErrorContainer = styled(FlexColumn) <{ css?: FlattenInterpolation<ThemeProps<ThemeType>> | string }>`
  ${({ theme }) => `gap: ${theme.padding[2]}`};
  ${({ css }) => css};
`;

export default function Section(props: SectionProps) {
  const {
    id = '',
    refName = '',
    propertyName = '',
    isLocked,
    children,
    errors,
    warnings,
    titleIcon,
    CollapsibleComponent = Collapsible,
    errorContainerCss,
    level = 0,
    ...rest
  } = props;

  const refByRefName = useCreateAndAddRefs(refName);
  const refById = useCreateAndAddRefs(`${id}${propertyName}`);

  const sectionErrors = flattenErrorTypeArray(errors?.[id] ?? []);
  const sectionWarnings = flattenErrorTypeArray(warnings?.[id] ?? []);

  const filteredErrors = useMemo(
    () => (!propertyName
      ? groupErrorsByNonProperty(sectionErrors)
      : groupErrorsByProperty(sectionErrors.filter((e) => e.propertyName === propertyName))
    ),
    [sectionErrors],
  );

  const rejectDataErrors = filteredErrors?.[ErrorTypes.RejectData];
  const sortedRejectDataErrors = rejectDataErrors?.slice()?.sort((de) => {
    if (de.errorCode === 'NoProducts') { return 1; }
    return -1;
  });
  const validationFailureErrors = filteredErrors?.[ErrorTypes.ValidationFailure];

  const filteredWarnings = useMemo(
    () => (!propertyName
      ? groupErrorsByNonProperty(sectionWarnings)
      : groupErrorsByProperty(sectionWarnings.filter((e) => e.propertyName === propertyName))
    ),
    [sectionWarnings],
  );

  const referWarnings = filteredWarnings?.[ErrorTypes.Refer];
  const warningWarnings = filteredWarnings?.[ErrorTypes.Warning];

  const filteredPropertyErrors = useMemo(
    () => groupErrorsByProperty(sectionErrors),
    [sectionErrors],
  );

  const validationFailurePropertyErrors = filteredPropertyErrors?.[ErrorTypes.ValidationFailure];

  const { isReadOnly } = usePageConfigurationContext();
  const isSectionLocked = isReadOnly && level === 0;

  return (
    <div id={id} ref={refById}>
      <div ref={refByRefName}>
        <CollapsibleComponent
          id={`${id}-collapsible`}
          titleIcon={isSectionLocked ? <LockIcon /> : titleIcon ?? (
            <SectionTitleIcon
              errors={[
                ...(validationFailureErrors ?? []),
                ...(validationFailurePropertyErrors ?? []),
                ...(rejectDataErrors ?? []),
              ]}
              isLocked={isLocked}
            />
          )}
          level={level}
          {...rest}
        >
          <ErrorContainer css={errorContainerCss}>
            {rejectDataErrors?.length > 0 && (
              <WarningInfoDanger>
                <InfoList
                  items={sortedRejectDataErrors?.map((de) => (
                    de.errorMessage
                  ))}
                />
              </WarningInfoDanger>
            )}

            {validationFailureErrors?.length > 0 && (
              <WarningInfoDanger>
                <InfoList
                  items={validationFailureErrors?.map((de) => (
                    de.errorMessage
                  ))}
                />
              </WarningInfoDanger>
            )}

            {referWarnings?.length > 0 && (
              <WarningInfo>
                <InfoList items={referWarnings?.map((fw) => (
                  fw.errorMessage
                ))}
                />
              </WarningInfo>
            )}

            {warningWarnings?.length > 0 && (
              <WarningInfo>
                <InfoList items={warningWarnings?.map((fw) => (
                  fw.errorMessage
                ))}
                />
              </WarningInfo>
            )}
          </ErrorContainer>
          {children}
        </CollapsibleComponent>
      </div>
    </div>
  );
}

export function PaddedSection(props: SectionProps) {
  const { children } = props;
  return <Section {...props} CollapsibleComponent={PaddedCollapsible}>{children}</Section>;
}

const HeadlessWrapper = styled.div``;

const HeadlessHeader = styled(Flex)`${({ theme }) => `
  align-items: center;
  gap: ${theme.padding[1]};
  padding: ${theme.padding[1]} ${theme.padding[7]};
  border-radius: ${theme.borderRadius[1]};
  background: ${theme.colours.neutral[7]};

  color: ${theme.textPrimary};
  font-size: ${theme.fontSize[1]};
  line-height: ${theme.fontSize[3]};
  font-weight: ${theme.fontWeight.bold};
`}`;

function HeadlessCollapsible(props: ComponentProps<typeof Collapsible>) {
  const {
    children, title, id, tooltip, ...rest
  } = props;

  return (
    <HeadlessWrapper {...rest}>
      <HeadlessHeader>
        <p>
          {title}
        </p>
        {tooltip && (
          <>
            <TooltipInfoIcon id={`tooltip-${id}`} />
            <Tooltip id={`tooltip-${id}`} place="top">{tooltip}</Tooltip>
          </>
        )}
      </HeadlessHeader>
      <div>
        {children}
      </div>
    </HeadlessWrapper>
  );
}

export function PureSection(props: SectionProps) {
  const { children } = props;
  return <Section {...props} CollapsibleComponent={HeadlessCollapsible}>{children}</Section>;
}
